import { type GetStaticProps, type NextPage } from "next";
import React, { useCallback, useMemo } from "react";
import {
  type Page as SanityPage,
  type Banner,
  getPage,
  getAnnouncementBanner,
  getCdpComparisonPages,
  type SimpleCdpComparisonPage,
} from "lib/sanity";
import { Page } from "components/page";
import { getPageMetaImage } from "utils";
import { Box, SimpleGrid } from "@chakra-ui/react";
import { SimpleCatalog } from "components/simple-catalog";
import { CdpVsCard } from "components/compare-cdps/cdp-vs-card";
import { Section } from "components/section";
import { SimpleTextHero, HeroCTA } from "components/hero";

type CompareCdpsPageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  data: SimpleCdpComparisonPage[];
};

const CompareCdps: NextPage<CompareCdpsPageProps> = ({ page, announcementBanner, data }) => {
  // Get unique vendor names to use as categories
  const categories: string[] = useMemo(() => {
    const uniqueNames = new Set(data.flatMap((d) => [d.firstCdpVendor.name, d.secondCdpVendor.name]));
    return Array.from(uniqueNames);
  }, [data]);

  // Can use this for both category and search filtering, since it's based on the same values
  const filterFunction = useCallback((page: SimpleCdpComparisonPage, matchString: string): boolean => {
    return (
      page.firstCdpVendor.name.toLowerCase().includes(matchString.toLowerCase()) ||
      page.secondCdpVendor.name.toLowerCase().includes(matchString.toLowerCase())
    );
  }, []);

  const renderItemsFunction = useCallback((items: SimpleCdpComparisonPage[]) => {
    return (
      <Box pb={8} width="100%">
        <SimpleGrid as="ul" columns={{ base: 1, md: 2, lg: 3 }} gap="small" listStyleType="none" pt="tiny">
          {items.map((item) => (
            <CdpVsCard key={item.slug} {...item} />
          ))}
        </SimpleGrid>
      </Box>
    );
  }, []);

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Section position="relative" my="0" overflow="hidden">
        <SimpleTextHero
          chipText={page.eyebrow}
          title={page.title}
          description={page.description}
          actions={<HeroCTA />}
          maxW="100%"
          position="relative"
          zIndex={1}
        />
      </Section>

      <SimpleCatalog
        data={data}
        categories={categories}
        categoriesHeading="Compare by CDP"
        searchPlaceholder="Search for a CDP..."
        renderItems={renderItemsFunction}
        onCategoryFilter={filterFunction}
        onSearch={filterFunction}
      />
    </Page>
  );
};

export const getStaticProps: GetStaticProps<CompareCdpsPageProps> = async () => {
  const [page, announcementBanner, data] = await Promise.all([
    getPage("/compare-cdps"),
    getAnnouncementBanner(),
    getCdpComparisonPages(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      data,
    },
    revalidate: 60,
  };
};

export default CompareCdps;
